import { router } from "@inertiajs/react"
import { AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

/**
 * OKS-63 — Error state shared by both analytics surfaces.
 *
 * Renders when `state === "error"` (overview) or `error === true`
 * (detail). The Retry button calls `router.reload({ only: [...] })`
 * with the caller-supplied partial-reload key set so the surface
 * recovers without a full nav.
 */
export interface AnalyticsErrorStateProps {
  /**
   * Inertia partial-reload key set passed to `router.reload`. Matches
   * the controller's prop bag keys for the active surface so a
   * successful retry swaps the data without re-running unrelated
   * shared-props middleware.
   */
  reloadOnly: string[]
}

export function AnalyticsErrorState({ reloadOnly }: AnalyticsErrorStateProps) {
  const { t } = useTranslation()

  const retry = () => {
    router.reload({ only: reloadOnly })
  }

  return (
    <Card
      data-testid="analytics-error-state"
      className="mx-auto flex max-w-md flex-col items-center gap-3 p-6 text-center"
    >
      <CardContent className="flex flex-col items-center gap-3 p-0">
        <AlertTriangle
          className="text-destructive h-8 w-8"
          aria-hidden="true"
        />
        <h2 className="text-foreground text-base font-semibold">
          {t("analytics.error.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("analytics.error.body")}
        </p>
        <Button
          variant="outline"
          onClick={retry}
          data-testid="analytics-error-state-retry"
        >
          {t("analytics.error.retry")}
        </Button>
      </CardContent>
    </Card>
  )
}
